import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { toMention, type Mention } from "@/shared/types/mention";

/**
 * O texto INTEIRO de uma menção (2026-10-19): a listagem traz só o trecho
 * de 600 caracteres, e o detalhe (`mentionDetail`) traz a matéria toda em
 * `excerpt`, com o negrito dos termos calculado pelo servidor sobre ela.
 * Dono único da busca: o card da lista (texto no lugar do trecho) e o
 * modal do Modo revisão usam este hook.
 *
 * `load()` busca UMA vez: chamar de novo com a busca em curso ou já
 * concluída não faz nada, então recolher e abrir outra vez não pede de
 * novo. Falha guarda a mensagem em `error` e libera uma nova tentativa.
 * Trocar de menção (ou de token) zera tudo e descarta a resposta que
 * estava no ar.
 */
export function useMentionFullText(mention: Pick<Mention, "id">, token: string) {
  const [full, setFull] = useState<Mention | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    started.current = false;
    setFull(null);
    setLoading(false);
    setError(null);
  }, [token, mention.id]);

  const load = useCallback(async () => {
    if (started.current) return;
    started.current = true;
    const mine = generation.current;
    setLoading(true);
    setError(null);
    try {
      const loaded = toMention(await apiClient.mentionDetail(token, mention.id));
      if (mine === generation.current) setFull(loaded);
    } catch (failure) {
      if (mine === generation.current) {
        started.current = false;
        setError(errorMessage(failure, "Não foi possível carregar o texto completo."));
      }
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, [token, mention.id]);

  return { full, loading, error, load };
}
